/**
 * Analytics consent sync (task 461).
 *
 * Bridges the AMS-backed preference state to the analytics gate in
 * `lib/analytics.ts`. After auth init the app fetches the signed-in User's
 * preferences once (the same GET the preference center uses), caches the
 * result in memory for the session, and closes the analytics gate when
 * `analytics_tracking` is opted out.
 *
 * The preference center calls `applyPreferenceState` with every server
 * response so an in-session toggle change flips the gate live — the cache and
 * the gate always reflect recorded truth from AMS, never an optimistic value.
 *
 * Failure policy: `analytics_tracking` uses the opt-out model (default opted
 * in, disclosed in the Privacy Policy), so a failed fetch leaves the gate in
 * its current state rather than blocking the app.
 */

import { consentApi, PreferenceState } from '../services/api';
import { setAnalyticsConsent } from './analytics';

let cachedPreferences: PreferenceState | null = null;
let inflight: Promise<PreferenceState | null> | null = null;

/** The session-cached preference state, if fetched. */
export const getCachedPreferences = (): PreferenceState | null =>
  cachedPreferences;

/**
 * Record a server-returned preference state: update the session cache and
 * apply the `analytics_tracking` choice to the analytics gate immediately.
 */
export const applyPreferenceState = (preferences: PreferenceState): void => {
  cachedPreferences = preferences;
  setAnalyticsConsent(preferences.analytics_tracking !== 'opt_out');
};

/**
 * Fetch the caller's preferences (once per session — concurrent callers share
 * one request, later callers get the cache) and apply the analytics choice.
 * Returns the state, or null when the fetch failed (gate left unchanged).
 */
export const syncAnalyticsConsent = async (): Promise<PreferenceState | null> => {
  if (cachedPreferences) return cachedPreferences;

  if (!inflight) {
    inflight = consentApi
      .getPreferences()
      .then((res) => res.data.data)
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
  }

  const preferences = await inflight;
  if (preferences) {
    applyPreferenceState(preferences);
  }
  return preferences;
};

/**
 * Drop the session cache on sign-out so the next User's choices are fetched
 * fresh (the analytics identity is cleared separately by
 * `resetAnalyticsIdentity`).
 */
export const clearPreferenceCache = (): void => {
  cachedPreferences = null;
};
